import { useState } from 'react';

import { diaDeLima, sumarDias } from './formato';
import {
  ATAJOS_DE_VENCIMIENTO,
  cuantoFalta,
  estadoDelVencimiento,
  fechaDeCalendario,
  vencimientoDeLaCaja,
  vencimientoSugerido,
} from './vencimiento';

/**
 * La fecha de vencimiento de lo que entra, sin pelearse con el calendario.
 *
 * Un selector de fecha pelado pedía abrir el calendario y contar casillas por
 * cada caja de leche. Aquí hay atajos —«+1 semana», «+1 mes»— y, si la ficha
 * del insumo dice cuánto dura, esa propuesta a un toque. Y se dice en palabras
 * qué se eligió: una fecha que ya pasó entraba igual y nadie lo notaba hasta
 * que el lote salía vencido en la pantalla de Lotes.
 */
export function CampoDeVencimiento({
  id,
  valor,
  onCambio,
  sinFecha,
  onSinFecha,
  vidaUtilDias = null,
  permiteSinFecha = true,
  margenDias = 3,
}: {
  id: string;
  valor: string;
  onCambio: (valor: string) => void;
  sinFecha: boolean;
  onSinFecha: (sinFecha: boolean) => void;
  /** Lo que dura según su ficha. Nulo si no lo dice. */
  vidaUtilDias?: number | null;
  /**
   * Falso donde el negocio exige el vencimiento de cada lote (la botica): el
   * servidor no dejaría entrar un lote sin fecha, así que no se ofrece.
   */
  permiteSinFecha?: boolean;
  /**
   * Desde cuántos días antes se avisa «úsalo primero»: el `expiryAlertDays`
   * del negocio. Eran tres fijos, y a la botica —que avisa a noventa— un
   * lote que vencía en un mes le entraba sin decir nada.
   */
  margenDias?: number;
}) {
  const hoy = diaDeLima();
  const sugerida = vencimientoSugerido(hoy, vidaUtilDias);
  const estado = valor === '' ? null : estadoDelVencimiento(valor, hoy, margenDias);
  // Lo que se escribe tal como viene impreso: «EXP 11/2027».
  const [deLaCaja, setDeLaCaja] = useState('');
  const leidoDeLaCaja = deLaCaja.trim() === '' ? null : vencimientoDeLaCaja(deLaCaja);

  return (
    <div className="vencimiento">
      {/*
       * Un día, no un instante. Mandarlo con hora hace que la zona horaria le
       * reste uno, y el local acaba tirando comida buena antes de tiempo.
       */}
      <input
        id={id}
        type="date"
        value={valor}
        disabled={sinFecha}
        onChange={(evento) => {
          setDeLaCaja('');
          onCambio(evento.target.value);
        }}
      />
      {/*
       * Como viene en la caja: mes y año, que vence el último día de ese mes.
       * Poner el día 1 le quitaba un mes de vida a cada lote.
       */}
      <input
        type="text"
        className="vencimiento__de-la-caja"
        aria-label="Vencimiento como viene en la caja"
        placeholder="o como en la caja: 11/2027"
        value={deLaCaja}
        disabled={sinFecha}
        onChange={(evento) => {
          setDeLaCaja(evento.target.value);
          const fecha = vencimientoDeLaCaja(evento.target.value);
          if (fecha !== null) onCambio(fecha);
        }}
      />
      {deLaCaja.trim() !== '' && leidoDeLaCaja === null ? (
        <p className="nota-sm nota--pegada">Escríbelo como en la caja: mes y año, 11/2027.</p>
      ) : null}

      <div className="vencimiento__atajos" role="group" aria-label="Atajos de vencimiento">
        {sugerida === null ? null : (
          <button
            type="button"
            className={`opcion ${valor === sugerida ? 'opcion--activa' : ''}`}
            disabled={sinFecha}
            onClick={() => onCambio(sugerida)}
          >
            Según su ficha: {vidaUtilDias} días
          </button>
        )}
        {ATAJOS_DE_VENCIMIENTO.map((atajo) => {
          const fecha = sumarDias(hoy, atajo.dias);
          return (
            <button
              key={atajo.dias}
              type="button"
              className={`opcion ${valor === fecha ? 'opcion--activa' : ''}`}
              disabled={sinFecha}
              onClick={() => onCambio(fecha)}
            >
              {atajo.texto}
            </button>
          );
        })}
        {permiteSinFecha ? (
          <button
            type="button"
            className={`opcion ${sinFecha ? 'opcion--activa' : ''}`}
            aria-pressed={sinFecha}
            onClick={() => {
              onSinFecha(!sinFecha);
              onCambio('');
            }}
          >
            No tiene fecha
          </button>
        ) : null}
      </div>

      {estado === null ? null : estado === 'vencido' ? (
        <p className="aviso-caja aviso-caja--peligro" role="alert">
          Esa fecha ya pasó ({cuantoFalta(valor, hoy)}): entraría vencido. Revisa el envase.
        </p>
      ) : (
        <p className={`nota-sm nota--pegada${estado === 'bien' ? '' : ' vencimiento--pronto'}`}>
          Vence el {fechaDeCalendario(valor)} · {cuantoFalta(valor, hoy)}
          {estado === 'hoy' || estado === 'pronto' ? ': úsalo primero.' : ''}
        </p>
      )}
    </div>
  );
}
